import { Add, DeleteOutline, FlipToFront } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MyPaper, SectionPaper, TextField } from "../../../components";
import { TextOnlyEditor } from "../../../HOCs";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useUpdateQuoteMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { TermsAndConditionDetail, updateQuoteSlice } from "../../../redux/slices/quote";
import { AppRoutes } from "../../../router/routes";
import { reorder, UUIDv4 } from "../../../utils";
import { HEADING_LIMIT, NOTE_LIMIT, preparePayload, quoteTextEditorButtonList, SECTION_LIMIT } from "../utils";


export const QuoteContents: React.FC<{ quoteId: string, readOnly?: boolean, setIsSaved: any, refetch: () => void }> = ({ quoteId, readOnly, setIsSaved, refetch }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const { quote } = useAppSelector(store => store);

    const [updateQuote, updateQuoteData] = useUpdateQuoteMutation();

    const [titles, setTitles] = React.useState<TermsAndConditionDetail[]>(quote.contents);
    const [contents, setContents] = React.useState<TermsAndConditionDetail[]>(quote.contents);

    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to Update Quote Contents. Try Again Later!`;
        let errorData: any = updateQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        return message;
    };

    const saveItemDetails = () => {

        const copyQuote = copy(quote);
        copyQuote.contents = titles.map((title, index) => ({ notes: contents[index].notes, heading: title.heading, id: title.id }));
        let payload = { ...preparePayload(copyQuote) };

        dispatch(openBackdrop('Updating....'));
        updateQuote({ id: quoteId, payload }).then((res: any) => {
            if (res.data) {
                setIsSaved(true);
                dispatch(updateQuoteSlice(copyQuote));
                enqueueSnackbar('Quote Contents Updated Successfully!', { variant: "success" });
            } else {
                refetch();
                console.log('error:', res.error)
                enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
            }
            dispatch(closeBackdrop());
        });

        setIsSaved(true);
        setSearchParams({});
    };

    // To Add Contents Details
    const addQuoteContentDetails = () => {
        if ((titles ?? []).length >= SECTION_LIMIT) {
            enqueueSnackbar(
                `You can only have up to ${SECTION_LIMIT} Contents. Adding more isn't allowed.`,
                { variant: "warning" }
            );
            return;
        }

        const id = UUIDv4();

        let newTitles: TermsAndConditionDetail[] = copy(titles);
        newTitles.push({ id });

        let newContents: TermsAndConditionDetail[] = copy(contents);
        newContents.push({ id });

        setIsSaved(false);
        setTitles(newTitles);
        setContents(newContents);
    };

    // To Delete Contents
    const deleteQuoteContents = (id: string) => {
        let newTitles = copy(titles);
        newTitles = newTitles.filter(_ => _.id !== id);
        let newContents = copy(contents);
        newContents = newContents.filter(_ => _.id !== id);
        setIsSaved(false);
        setTitles(newTitles);
        setContents(newContents);
    };

    const onTitleUpdate = (id: string, value: string) => {
        setIsSaved(false);
        setTitles((prevTitles) => prevTitles.map(_ => {
            if (_.id === id) {
                return { ..._, heading: value }
            } else {
                return { ..._ }
            }
        }));
    };

    const onContentUpdate = (id: string, value: string) => {
        setIsSaved(false);
        setContents((prevTitles) => prevTitles.map(_ => {
            if (_.id === id) {
                return { ..._, notes: value }
            } else {
                return { ..._ }
            }
        }));
    };

    const onDragEnd = (result: any) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const newTitles = reorder(
            titles,
            result.source.index,
            result.destination.index
        );

        const newContents = reorder(
            contents,
            result.source.index,
            result.destination.index
        );

        setTitles(newTitles);
        setContents(newContents);
    };

    React.useEffect(() => {
        setTitles(quote.contents);
        setContents(quote.contents);
        // eslint-disable-next-line
    }, [quote]);

    React.useEffect(() => {
        setIsSaved(true);
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            saveItemDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    return (
        <Stack sx={{ gap: 1 }}>
            {/* Title */}
            <SectionPaper title="Contents" isExpand={false} hideExpandCollapse renderButtons={[
                <Button disabled={readOnly} startIcon={<Add />} variant="outlined" onClick={() => addQuoteContentDetails()}>Add Content</Button>
            ]}>
                <></>
            </SectionPaper>

            {/* Contents */}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable-contents">
                    {(provided: any, _: any) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            <Stack gap={1}>
                                {titles.map((content, index) => {
                                    return <Draggable
                                        key={index}
                                        draggableId={`draggable-${index + 1}-contents`}
                                        index={index}
                                    >
                                        {(provided: any, _: any) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                key={`key${index + 1}`}
                                                id={`div${index + 1}`}
                                            >
                                                <SectionPaper
                                                    key={content.id}
                                                    mdHeaderSize={9}
                                                    title={
                                                        <Stack
                                                            {...provided.dragHandleProps}
                                                            sx={{
                                                                background: (theme) =>
                                                                    _.isDragging
                                                                        ? theme.palette.background.default
                                                                        : "unset",
                                                            }} alignItems={"center"} direction={"row"} gap={1}>
                                                            {!readOnly &&
                                                                <Tooltip title="Drag Up/Down">
                                                                    <FlipToFront
                                                                        sx={{ marginTop: 0.5, cursor: "drag" }}
                                                                    />
                                                                </Tooltip>
                                                            }
                                                            <Typography variant="body1" sx={{
                                                                fontFamily: "htrts_bold",
                                                            }}>
                                                                {content.heading ? content.heading : `Heading ${index + 1}`}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                    renderButtons={readOnly ? [] : [
                                                        <IconButton onClick={() => deleteQuoteContents(content.id)}>
                                                            <DeleteOutline color={"error"} />
                                                        </IconButton>
                                                    ]}
                                                >
                                                    <Stack sx={{ gap: 2 }}>
                                                        <TextField
                                                            id="quote-content-heading"
                                                            value={content.heading}
                                                            label={`Heading ${content.heading?.length || 0}/${HEADING_LIMIT}`}
                                                            size="small"
                                                            placeholder="Enter heading"
                                                            onChange={(e) => {
                                                                if (e?.target?.value?.length <= HEADING_LIMIT) {
                                                                    onTitleUpdate(content.id, e.target.value)
                                                                } else {
                                                                    enqueueSnackbar(
                                                                        `You can only have up to ${HEADING_LIMIT} characters on Heading. Adding more isn't allowed.`,
                                                                        { variant: "warning" }
                                                                    );
                                                                }
                                                            }}
                                                            viewOnly={readOnly}
                                                        />

                                                        <TextOnlyEditor
                                                            placeholder="Enter note"
                                                            setDefaultStyle="border: none !important"
                                                            defaultValue={contents[index].notes}
                                                            onChange={(note: string) => onContentUpdate(content.id, note)}
                                                            readOnly={readOnly}
                                                            disable={readOnly}
                                                            height="250px"
                                                            setOptions={{
                                                                buttonList: quoteTextEditorButtonList,
                                                                maxCharCount: NOTE_LIMIT,
                                                                defaultStyle: "font-family: 'hrts_regular'",
                                                            }}
                                                        />
                                                    </Stack>
                                                </SectionPaper>
                                            </div>
                                        )}
                                    </Draggable>
                                })}
                            </Stack>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Alert Box */}
            {(updateQuoteData.isError) && <Alert sx={{ mt: 2 }} severity="error">{giveMeErrorMessage()}</Alert>}

            {
                !readOnly && <MyPaper padding={0} height={"70px"}>
                    <Box sx={{ paddingX: 2 }}>

                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                p: 2
                            }}>
                            {/* Cancel Button */}
                            <Button disabled={updateQuoteData.isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => navigate(AppRoutes.allQuickQuotes)} >
                                Cancel
                            </Button>

                            {/* Create Button */}
                            <LoadingButton loading={updateQuoteData.isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={saveItemDetails}>
                                {"Save"}
                            </LoadingButton>
                        </Stack>
                    </Box>
                </MyPaper>
            }


        </Stack>
    );
}